import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Contract } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { policyService, Policy } from '../services/policyService';
import { useLanguage } from '../contexts/LanguageContext';

interface SidebarProps {
  history: Contract[];
  activeContractId?: string;
  onSelectContract: (contract: Contract, opts?: { fromHistory?: boolean }) => void;
  onNewProject: () => void;
  companyPolicy: string;
  setCompanyPolicy: (policy: string) => void;
  onBack: () => void;
  onLogout: () => void;
  isSidebarVisible?: boolean;
  onToggleSidebar?: () => void;
  onPolicyManagerToggle?: (isOpen: boolean) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  history,
  activeContractId,
  onSelectContract,
  onNewProject,
  companyPolicy,
  setCompanyPolicy,
  onBack,
  onLogout,
  isSidebarVisible = true,
  onToggleSidebar,
  onPolicyManagerToggle
}) => {
  const { t, lang, setLang } = useLanguage();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPolicyManager, setShowPolicyManager] = useState(false);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);
  const [newPolicyName, setNewPolicyName] = useState('');
  const [newPolicyContent, setNewPolicyContent] = useState('');
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPopupRect, setMenuPopupRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const { user } = useAuth();

  const userName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
  const avatarUrl = user?.user_metadata?.avatar_url || '/b9b399097f4b5c7e4ac00b2066670164.jpg';

  useEffect(() => {
    if (showPolicyManager) {
      policyService.getAllPolicies().then(setPolicies);
    }
  }, [showPolicyManager]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        if (target.closest('[data-sidebar-portal]')) return;
        setShowUserMenu(false);
        if (showPolicyManager) {
          onPolicyManagerToggle?.(false);
        }
        setShowPolicyManager(false);
        setEditingPolicy(null);
        setIsCreatingNew(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPolicyManager, onPolicyManagerToggle]);

  useLayoutEffect(() => {
    if (!showUserMenu) {
      setMenuPopupRect(null);
      return;
    }
    const el = menuRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setMenuPopupRect({ top: r.top, left: r.left, width: r.width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [showUserMenu, showPolicyManager]);

  const handleSavePolicy = async () => {
    if (editingPolicy) {
      await policyService.updatePolicy(editingPolicy.id, newPolicyName, newPolicyContent);
    } else if (isCreatingNew && newPolicyName.trim()) {
      await policyService.createPolicy(newPolicyName, newPolicyContent);
    }
    const updated = await policyService.getAllPolicies();
    setPolicies(updated);
    setEditingPolicy(null);
    setIsCreatingNew(false);
    setNewPolicyName('');
    setNewPolicyContent('');
  };

  const handleDeletePolicy = async (id: string) => {
    await policyService.deletePolicy(id);
    const updated = await policyService.getAllPolicies();
    setPolicies(updated);
  };

  const startEdit = (policy: Policy) => {
    setEditingPolicy(policy);
    setNewPolicyName(policy.name);
    setNewPolicyContent(policy.content);
    setIsCreatingNew(false);
  };

  const startCreate = () => {
    setIsCreatingNew(true);
    setEditingPolicy(null);
    setNewPolicyName('');
    setNewPolicyContent('');
  };

  // 如果侧边栏隐藏，显示简化版本（悬浮状态）
  if (!isSidebarVisible) {
    return (
      <div className="absolute left-4 top-4" style={{ zIndex: 9999 }}>
        {onToggleSidebar && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleSidebar();
            }}
            className="w-12 h-12 flex items-center justify-center cursor-pointer hover:opacity-80 transition-all rounded-2xl"
            style={{
              background: 'rgba(207, 211, 216, 0.1)',
              backdropFilter: 'blur(40px) saturate(180%)',
              WebkitBackdropFilter: 'blur(40px) saturate(180%)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
            }}
            title="显示侧边栏"
          >
            <img src="/左侧边栏.png" alt="侧边栏图标" className="w-6 h-6" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className="w-72 flex-shrink-0 flex flex-col relative z-20 h-full border-r border-slate-200/50 rounded-2xl overflow-hidden"
      style={{
        background: 'rgba(255, 255, 255, 0.92)',
        backdropFilter: 'blur(20px) saturate(160%)',
        WebkitBackdropFilter: 'blur(20px) saturate(160%)',
        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.8)'
      }}
    >
      <div className="h-20 flex items-center px-6 border-b border-slate-200/30">
        <div className="flex items-center gap-3 cursor-pointer group flex-1" onClick={onBack} title="Back to Home">
          <div className="w-9 h-9 rounded-xl overflow-hidden shadow-lg shadow-slate-200/50 transition-transform group-hover:scale-105">
            <img src="/docsure logo.png" alt="DocSure Logo" className="w-full h-full object-cover" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-800 leading-tight group-hover:text-blue-600 transition-colors">DocSure AI</h2>
            <div className="text-[10px] items-center flex gap-1 font-bold uppercase tracking-widest relative overflow-hidden">
              <span className="bg-gradient-to-r from-slate-400 via-slate-200 to-slate-400 bg-[length:200%_auto] animate-shine bg-clip-text text-transparent">
                Contract Review
              </span>
            </div>
          </div>
        </div>
        {onToggleSidebar && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleSidebar();
            }}
            className="w-6 h-6 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity ml-2"
            title="隐藏侧边栏"
          >
            <img src="/左侧边栏.png" alt="侧边栏图标" className="w-6 h-6" />
          </button>
        )}
      </div>

      <div className="p-4 pb-0">
        <button
          onClick={onNewProject}
          className="w-full py-2.5 rounded-xl bg-white border border-slate-200 text-slate-600 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50/50 transition-all font-semibold text-sm flex items-center justify-center gap-2 shadow-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          {t('nav.newProject')}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
        {history.length === 0 ? (
          <div className="h-40 flex flex-col items-center justify-center text-slate-400 border-2 border-dashed border-slate-100 rounded-2xl">
            <p className="text-xs font-medium">{t('nav.noRecords')}</p>
          </div>
        ) : (
          history.map((item) => {
            const isActive = activeContractId === item.id;
            return (
              <button
                key={item.id}
                onClick={() => onSelectContract(item, { fromHistory: true })}
                className={`w-full group text-left p-3.5 rounded-xl transition-all duration-300 relative overflow-hidden ${isActive
                    ? 'bg-white shadow-lg shadow-slate-100 ring-1 ring-black/5'
                    : 'hover:bg-white/60 hover:shadow-md hover:shadow-slate-50 border border-transparent hover:border-white/50'
                  }`}
              >
                <div className="flex justify-between items-start mb-1">
                  <span className={`text-sm font-bold truncate pr-3 transition-colors ${isActive ? 'text-slate-900' : 'text-slate-600 group-hover:text-slate-900'}`}>
                    {item.name}
                  </span>
                  <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shadow-sm ${item.status === 'completed' ? 'bg-emerald-500 shadow-emerald-200' : 'bg-amber-500 shadow-amber-200'}`} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide group-hover:text-slate-500 transition-colors">
                    {item.uploadDate}
                  </span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded text-slate-500 bg-slate-100 group-hover:bg-slate-200 transition-colors">
                    {item.clauses.length} {t('nav.items')}
                  </span>
                </div>
              </button>
            );
          })
        )}
      </div>

      <div className="p-4 border-t border-slate-200/40 bg-white/30 backdrop-blur-md relative" ref={menuRef}>
        <button
          onClick={() => {
            const next = !showUserMenu;
            setShowUserMenu(next);
            if (next && menuRef.current) {
              const r = menuRef.current.getBoundingClientRect();
              setMenuPopupRect({ top: r.top, left: r.left, width: r.width });
            }
          }}
          className="w-full flex items-center gap-3 p-2 hover:bg-white/60 transition-all group relative z-10"
          style={{
            borderRadius: '20px'
          }}
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-slate-200 to-slate-100 p-0.5 shadow-sm group-hover:shadow-md transition-all overflow-hidden">
            <img src={avatarUrl} alt="User" className="w-full h-full rounded-full object-cover" />
          </div>
          <div className="flex-1 text-left min-w-0">
            <div className="text-sm font-bold text-slate-800 group-hover:text-blue-600 transition-colors truncate">{userName}</div>
            <div className="text-[10px] font-medium text-slate-500 truncate">{user ? 'Pro Plan · Active' : 'Basic Plan'}</div>
          </div>
          <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-slate-400 shadow-sm transition-all transform group-hover:-translate-y-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
            </svg>
          </div>
        </button>

        {showUserMenu && menuPopupRect && createPortal(
          <div
            data-sidebar-portal
            className="fixed z-[9999] rounded-2xl overflow-hidden p-3 border border-white/40 shadow-2xl shadow-slate-900/10 sidebar-popup"
            style={{
              top: menuPopupRect.top - 12,
              left: menuPopupRect.left + 16,
              width: menuPopupRect.width - 32,
              transform: 'translateY(-100%)',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(24px) saturate(160%)',
              WebkitBackdropFilter: 'blur(24px) saturate(160%)',
              boxShadow: '0 24px 48px -12px rgba(0,0,0,0.12), 0 0 0 1px rgba(255,255,255,0.5) inset'
            }}
          >
            {!showPolicyManager ? (
              <>
                <div className="relative mb-3">
                  <button
                    onClick={() => {
                      setShowPolicyManager(true);
                      onPolicyManagerToggle?.(true);
                    }}
                    className="w-full flex items-center gap-3 py-2.5 px-3 rounded-xl text-left bg-slate-50 hover:bg-slate-100 transition-all"
                  >
                    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-500 flex items-center justify-center">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-slate-700">{t('nav.managePolicies')}</p>
                      <p className="text-[10px] text-slate-400 truncate">
                        {policies.length > 0 ? `${policies.length} ${t('nav.items')}` : t('nav.notSet')}
                      </p>
                    </div>
                    <svg className="w-4 h-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                <div className="mb-3 pt-2 border-t border-slate-100">
                  <button
                    onClick={onLogout}
                    className="w-full flex items-center gap-3 py-2 px-3 rounded-xl text-left hover:bg-red-50 text-slate-500 hover:text-red-600 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span className="text-xs font-bold">{t('nav.signOut')}</span>
                  </button>
                </div>

                <div className="relative bg-slate-100/80 p-1 rounded-xl flex cursor-pointer">
                  <div
                    className="absolute top-1 bottom-1 rounded-lg bg-white shadow-sm transition-all duration-300 ease-out"
                    style={{ left: lang === 'zh' ? '4px' : '50%', width: 'calc(50% - 4px)' }}
                  />
                  <button onClick={() => setLang('zh')} className={`relative z-10 flex-1 py-2 text-xs font-bold text-center transition-colors ${lang === 'zh' ? 'text-slate-800' : 'text-slate-400'}`}>
                    中文
                  </button>
                  <button onClick={() => setLang('en')} className={`relative z-10 flex-1 py-2 text-xs font-bold text-center transition-colors ${lang === 'en' ? 'text-slate-800' : 'text-slate-400'}`}>
                    EN
                  </button>
                </div>
              </>
            ) : (
              <div className="relative">
                <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setShowPolicyManager(false);
                        setEditingPolicy(null);
                        setIsCreatingNew(false);
                        onPolicyManagerToggle?.(false);
                      }}
                      className="w-6 h-6 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <p className="text-xs font-bold text-slate-700">{t('nav.managePolicies')}</p>
                  </div>
                  {!isCreatingNew && !editingPolicy && (
                    <button
                      onClick={startCreate}
                      className="px-2.5 py-1 bg-blue-500 hover:bg-blue-600 text-white text-[10px] font-bold rounded-lg flex items-center gap-1"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                      </svg>
                      {t('policy.addNew')}
                    </button>
                  )}
                </div>

                {(isCreatingNew || editingPolicy) && (
                  <div className="mb-3 space-y-2">
                    <input
                      type="text"
                      value={newPolicyName}
                      onChange={(e) => setNewPolicyName(e.target.value)}
                      placeholder={t('policy.ruleName')}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-blue-400"
                    />
                    <textarea
                      value={newPolicyContent}
                      onChange={(e) => setNewPolicyContent(e.target.value)}
                      placeholder={t('policy.ruleContent')}
                      rows={4}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs resize-none focus:outline-none focus:border-blue-400"
                    />
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => { setEditingPolicy(null); setIsCreatingNew(false); setNewPolicyName(''); setNewPolicyContent(''); }}
                        className="px-3 py-1.5 text-slate-500 text-[10px] font-bold hover:text-slate-700"
                      >
                        {t('policy.cancel')}
                      </button>
                      <button
                        onClick={handleSavePolicy}
                        disabled={!newPolicyName.trim()}
                        className="px-3 py-1.5 bg-slate-900 text-white text-[10px] font-bold rounded-lg hover:bg-black disabled:opacity-50"
                      >
                        {t('policy.save')}
                      </button>
                    </div>
                  </div>
                )}

                {!isCreatingNew && !editingPolicy && (
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {policies.length === 0 ? (
                      <div className="py-6 text-center text-slate-400">
                        <p className="text-xs">{t('policy.noPolicies')}</p>
                        <p className="text-[10px] mt-1">{t('policy.addFirst')}</p>
                      </div>
                    ) : (
                      policies.map(policy => (
                        <div key={policy.id} className="p-2.5 bg-slate-50 rounded-xl group hover:bg-slate-100 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-bold text-slate-700 truncate">{policy.name}</p>
                              <p className="text-[10px] text-slate-400 truncate">{policy.content.slice(0, 50)}...</p>
                            </div>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => startEdit(policy)}
                                className="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeletePolicy(policy.id)}
                                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>,
          document.body
        )}
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(0, 0, 0, 0.05); border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(0, 0, 0, 0.1); }
        @keyframes shine { to { background-position: 200% center; } }
        .animate-shine { animation: shine 3s linear infinite; }
        .sidebar-popup { opacity: 0; animation: sidebarPopupIn 0.18s ease-out forwards; }
        @keyframes sidebarPopupIn { to { opacity: 1; } }
      `}</style>
    </div>
  );
};
